import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MessageIcon from "@mui/icons-material/Message";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GroupsIcon from "@mui/icons-material/Groups";
import PushPinIcon from "@mui/icons-material/PushPin";
import api from "../api/client";

const POLL_INTERVAL_MS = 5000;

// Pinned group channels
const PINNED_GROUPS = [
  { id: "GROUP:ALL", name: "All Rangers", icon: "🌟", color: "#ca8a04" },
  { id: "GROUP:DALLAS", name: "Dallas Region", icon: "🤠", color: "#dc2626" },
  { id: "GROUP:CAPITOL", name: "Capitol Region", icon: "🏛️", color: "#2563eb" },
];

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.split(/[\s•]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getAvatarColor = (id) => {
  const colors = [
    "#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#9333ea",
    "#0891b2", "#c026d3", "#ea580c", "#4f46e5", "#059669"
  ];
  let hash = 0;
  for (let i = 0; i < (id || "").length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Sound notification for new messages
const playMessageSound = () => {
  try {
    const audio = new Audio("/sounds/cid_alarm.wav");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    console.warn("Could not play message sound:", e);
  }
};

export default function MessagesPanel({ open, onClose, currentUserId, lastReadMessageId }) {
  const [tab, setTab] = useState(0); // 0 = conversations, 1 = new message
  const [messages, setMessages] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const hasInitialLoadRef = useRef(false);

  // Fetch messages
  const fetchMessages = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get("/messages", { params: { _ts: Date.now() } });
      const newMessages = Array.isArray(response.data) ? response.data : [];
      
      // Debug: log raw messages from API
      if (newMessages.length > 0) {
        console.log("[MessagesPanel] Raw messages from API:", newMessages.map(m => ({
          id: m.id,
          senderRangerId: m.senderRangerId,
          recipientRangerId: m.recipientRangerId, 
          isOwnMessage: m.isOwnMessage,
          senderName: m.senderName
        })));
      }
      
      // Check for new incoming messages (not from self) to play sound
      if (hasInitialLoadRef.current && newMessages.length > 0) {
        const latestMessage = newMessages.reduce((latest, msg) => 
          !latest || new Date(msg.sentAtUtc) > new Date(latest.sentAtUtc) ? msg : latest
        , null);
        
        if (latestMessage && 
            !latestMessage.isOwnMessage && 
            lastMessageIdRef.current !== null &&
            latestMessage.id > lastMessageIdRef.current) {
          playMessageSound();
        }
      }
      
      // Track latest message ID
      if (newMessages.length > 0) {
        const maxId = Math.max(...newMessages.map(m => m.id));
        lastMessageIdRef.current = maxId;
      }
      
      hasInitialLoadRef.current = true;
      setMessages(newMessages);
      setError("");
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      if (showLoading) setError("Failed to load messages");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Fetch available recipients
  const fetchRecipients = useCallback(async () => {
    try {
      const response = await api.get("/messages/recipients", { params: { _ts: Date.now() } });
      setRecipients(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch recipients:", err);
    }
  }, []);

  // Initial load and polling
  useEffect(() => {
    if (!open) {
      // Clear polling when closed
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    fetchMessages();
    fetchRecipients();

    // Start polling
    pollIntervalRef.current = setInterval(() => {
      fetchMessages(false);
      fetchRecipients();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [open, fetchMessages, fetchRecipients]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (selectedConversation && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, selectedConversation]);

  // Check if a conversation ID is a pinned group
  const isPinnedGroup = (id) => PINNED_GROUPS.some(g => g.id === id);
  const getPinnedGroup = (id) => PINNED_GROUPS.find(g => g.id === id);

  // Group messages by conversation (case-insensitive keys)
  const conversations = useMemo(() => {
    const convMap = new Map();
    
    // Helper to normalize keys for case-insensitive matching
    const normalizeKey = (key) => (key || "").toUpperCase();
    
    // Initialize pinned groups (always show them)
    PINNED_GROUPS.forEach(group => {
      convMap.set(normalizeKey(group.id), {
        partnerId: group.id,
        partnerName: group.name,
        isGroup: true,
        groupInfo: group,
        messages: [],
        lastMessage: null,
        unreadCount: 0,
      });
    });
    
    messages.forEach((msg) => {
      // Check if this is a group message
      const isGroupMsg = isPinnedGroup(msg.recipientRangerId);
      
      let partnerId;
      let partnerName;
      
      if (isGroupMsg) {
        // Group messages: use the group as the conversation
        partnerId = msg.recipientRangerId;
        const group = getPinnedGroup(msg.recipientRangerId);
        partnerName = group?.name || msg.recipientRangerId;
      } else {
        // Direct messages: use the other person
        partnerId = msg.isOwnMessage ? msg.recipientRangerId : msg.senderRangerId;
        partnerName = msg.isOwnMessage ? msg.recipientRangerId : msg.senderName;
      }
      
      const key = normalizeKey(partnerId);
      if (!convMap.has(key)) {
        convMap.set(key, {
          partnerId,
          partnerName: partnerName || partnerId,
          isGroup: false,
          messages: [],
          lastMessage: null,
          unreadCount: 0,
        });
      }
      
      const conv = convMap.get(key);
      conv.messages.push(msg);
      
      if (!conv.lastMessage || new Date(msg.sentAtUtc) > new Date(conv.lastMessage.sentAtUtc)) {
        conv.lastMessage = msg;
      }
    });
    
    const allConvs = Array.from(convMap.values());
    
    // Debug: log conversations
    console.log("[MessagesPanel] Conversations:", allConvs.filter(c => !c.isGroup).map(c => ({
      partnerId: c.partnerId,
      partnerName: c.partnerName,
      messageCount: c.messages.length
    })));
    
    // Separate pinned groups and personal chats
    const pinnedGroups = PINNED_GROUPS.map(g => allConvs.find(c => c.partnerId === g.id)).filter(Boolean);
    const personalChats = allConvs
      .filter(c => !isPinnedGroup(c.partnerId))
      .sort((a, b) => {
        const aTime = a.lastMessage ? new Date(a.lastMessage.sentAtUtc).getTime() : 0;
        const bTime = b.lastMessage ? new Date(b.lastMessage.sentAtUtc).getTime() : 0;
        return bTime - aTime;
      });
    
    // Return pinned groups first (in order), then personal chats by most recent
    return [...pinnedGroups, ...personalChats];
  }, [messages]);

  // Get messages for selected conversation (case-insensitive match)
  const conversationMessages = useMemo(() => {
    if (!selectedConversation) return [];
    const normalizedSelected = selectedConversation.toUpperCase();
    const conv = conversations.find((c) => c.partnerId.toUpperCase() === normalizedSelected);
    return conv ? conv.messages.sort((a, b) => 
      new Date(a.sentAtUtc).getTime() - new Date(b.sentAtUtc).getTime()
    ) : [];
  }, [conversations, selectedConversation]);

  // Get selected conversation details (case-insensitive match)
  const selectedConvDetails = useMemo(() => {
    if (!selectedConversation) return null;
    const normalizedSelected = selectedConversation.toUpperCase();
    return conversations.find((c) => c.partnerId.toUpperCase() === normalizedSelected);
  }, [conversations, selectedConversation]);

  // Filtered recipients for new message
  const filteredRecipients = useMemo(() => {
    if (!searchQuery) return recipients;
    const query = searchQuery.toLowerCase();
    return recipients.filter((r) => 
      r.rangerId?.toLowerCase().includes(query) ||
      r.displayName?.toLowerCase().includes(query) ||
      r.division?.toLowerCase().includes(query)
    );
  }, [recipients, searchQuery]);

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await api.post("/messages", {
        recipientRangerId: selectedConversation,
        body: newMessage.trim(),
      });
      setNewMessage("");
      await fetchMessages(false);
    } catch (err) {
      console.error("Failed to send message:", err);
      const errorMsg = err.response?.data || "Failed to send message";
      setError(typeof errorMsg === "string" ? errorMsg : "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Start new conversation with recipient
  const handleSelectRecipient = (recipientId) => {
    setSelectedConversation(recipientId);
    setTab(0);
    setSearchQuery("");
  };

  // Handle key press in message input
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setSelectedConversation(null);
    setTab(0);
    setNewMessage("");
    setSearchQuery("");
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400 },
          bgcolor: "background.default",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        {selectedConversation ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => setSelectedConversation(null)}>
              <ArrowBackIcon />
            </IconButton>
            {selectedConvDetails?.isGroup ? (
              <>
                <Avatar sx={{ bgcolor: selectedConvDetails.groupInfo?.color, width: 32, height: 32 }}>
                  <GroupsIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {selectedConvDetails.groupInfo?.icon} {selectedConvDetails?.partnerName}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Avatar sx={{ bgcolor: getAvatarColor(selectedConversation), width: 32, height: 32 }}>
                  {getInitials(selectedConvDetails?.partnerName || selectedConversation)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {selectedConvDetails?.partnerName || selectedConversation}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <MessageIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              MESSAGES
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => { fetchMessages(); fetchRecipients(); }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Tabs (only when no conversation selected) */}
      {!selectedConversation && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Conversations" />
          <Tab label="New Message" />
        </Tabs>
      )}

      {/* Error Alert */}
      {error && (
        <Box sx={{ p: 1, bgcolor: "error.dark" }}>
          <Typography variant="body2" color="white" align="center">
            {error}
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : selectedConversation ? (
          // Conversation View
          <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 180px)" }}>
            {/* Messages */}
            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              {conversationMessages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" py={4}>
                  No messages yet. Start the conversation!
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {conversationMessages.map((msg) => {
                    // Highlight unread messages (not own, newer than lastReadMessageId)
                    const isNew = !msg.isOwnMessage && lastReadMessageId && msg.id > lastReadMessageId;
                    
                    return (
                    <Box
                      key={msg.id}
                      sx={{
                        display: "flex",
                        justifyContent: msg.isOwnMessage ? "flex-end" : "flex-start",
                        position: "relative",
                      }}
                    >
                      {/* NEW indicator for unread messages */}
                      {isNew && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 4,
                            height: "100%",
                            bgcolor: "error.main",
                            borderRadius: 1,
                          }}
                        />
                      )}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          maxWidth: "80%",
                          bgcolor: msg.isOwnMessage ? "primary.main" : isNew ? "error.dark" : "grey.800",
                          color: msg.isOwnMessage ? "primary.contrastText" : "text.primary",
                          borderRadius: msg.isOwnMessage
                            ? "16px 16px 4px 16px"
                            : "16px 16px 16px 4px",
                          border: isNew ? "1px solid" : "none",
                          borderColor: "error.main",
                        }}
                      >
                        {/* Show sender name for group messages (not own) */}
                        {selectedConvDetails?.isGroup && !msg.isOwnMessage && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontWeight: 700, 
                              color: getAvatarColor(msg.senderRangerId),
                              display: "block",
                              mb: 0.5
                            }}
                          >
                            {msg.senderName || msg.senderRangerId}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {msg.body}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" mt={0.5}>
                          <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.65rem" }}>
                            {formatTime(msg.sentAtUtc)}
                          </Typography>
                          {msg.isOwnMessage && (
                            <DoneAllIcon sx={{ fontSize: 12, opacity: 0.7 }} />
                          )}
                        </Stack>
                      </Paper>
                    </Box>
                  )})}
                  <div ref={messagesEndRef} />
                </Stack>
              )}
            </Box>

            {/* Message Input */}
            <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
              <TextField
                fullWidth
                size="small"
                placeholder={selectedConvDetails?.isGroup 
                  ? `Message ${selectedConvDetails.partnerName}...` 
                  : "Type a message..."}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
                multiline
                maxRows={3}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!newMessage.trim() || sending}
                      >
                        {sending ? <CircularProgress size={20} /> : <SendIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        ) : tab === 0 ? (
          // Conversations List
          <List disablePadding>
            {conversations.length === 0 ? (
              <Box py={4} textAlign="center">
                <MessageIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No conversations yet
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => setTab(1)}
                >
                  Start New Message
                </Button>
              </Box>
            ) : (
              conversations.map((conv, index) => {
                const isGroup = conv.isGroup;
                const isPinned = isPinnedGroup(conv.partnerId);
                const showDivider = isPinned &&
                  index === PINNED_GROUPS.length - 1 &&
                  conversations.length > PINNED_GROUPS.length;
                
                // Check if conversation has unread messages
                const hasUnread = conv.messages.some(msg => 
                  !msg.isOwnMessage && lastReadMessageId && msg.id > lastReadMessageId
                );

                return (
                  <Box key={conv.partnerId}>
                    <ListItemButton
                      onClick={() => setSelectedConversation(conv.partnerId)}
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: hasUnread ? "error.main" : "divider",
                        "&:hover": { bgcolor: "action.hover" },
                        bgcolor: hasUnread ? "rgba(211, 47, 47, 0.15)" : isPinned ? "action.selected" : "transparent",
                        borderLeft: hasUnread ? "4px solid" : "none",
                        borderLeftColor: "error.main",
                      }}
                    >
                      <ListItemAvatar>
                        {isGroup ? (
                          <Avatar sx={{ bgcolor: conv.groupInfo?.color }}>
                            <GroupsIcon />
                          </Avatar>
                        ) : (
                          <Avatar sx={{ bgcolor: getAvatarColor(conv.partnerId) }}>
                            {getInitials(conv.partnerName)}
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {isPinned && (
                              <PushPinIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            )}
                            <Typography variant="subtitle2" fontWeight={600} noWrap>
                              {isGroup ? `${conv.groupInfo?.icon} ${conv.partnerName}` : conv.partnerName}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 220 }}
                          >
                            {conv.lastMessage ? (
                              <>
                                {conv.lastMessage.isOwnMessage ? "You: " : isGroup ? `${conv.lastMessage.senderName || conv.lastMessage.senderRangerId}: ` : ""}
                                {conv.lastMessage.body}
                              </>
                            ) : (
                              isGroup ? "No messages yet" : "No messages"
                            )}
                          </Typography>
                        }
                      />
                      {conv.lastMessage && (
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(conv.lastMessage.sentAtUtc)}
                        </Typography>
                      )}
                    </ListItemButton>
                    {showDivider && (
                      <Divider>
                        <Chip label="Direct Messages" size="small" sx={{ fontSize: "0.7rem" }} />
                      </Divider>
                    )}
                  </Box>
                );
              })
            )}
          </List>
        ) : (
          // New Message - Recipients List
          <Box>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search rangers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
            <Divider />
            <List disablePadding>
              {filteredRecipients.length === 0 ? (
                <Box py={4} textAlign="center">
                  <PersonIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {recipients.length === 0
                      ? "No rangers online"
                      : "No matching rangers"}
                  </Typography>
                </Box>
              ) : (
                filteredRecipients.map((recipient) => (
                  <ListItemButton
                    key={recipient.rangerId}
                    onClick={() => handleSelectRecipient(recipient.rangerId)}
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: "success.main",
                              border: "2px solid",
                              borderColor: "background.paper",
                            }}
                          />
                        }
                      >
                        <Avatar sx={{ bgcolor: getAvatarColor(recipient.rangerId) }}>
                          {getInitials(recipient.displayName || recipient.rangerId)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={600}>
                          {recipient.displayName || recipient.rangerId}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          {recipient.division && (
                            <Chip
                              label={recipient.division}
                              size="small"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Online
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
